import { useSelector } from 'react-redux';
import { userSelectors } from '@/Redux/User';
import { useMemo, useState } from 'react';
import { TreeItemType } from '@/Components/TreeView/TreeView';
import { treeViewUtils } from '@/Utils';
import { MarketplaceType } from '@/Enums';
import { Pair } from '@/Types';

// Marketplace account tree view options
export function useMpAccountTreeViewOptions(
  selectedMpAccounts?: (string | number)[],
  excludedMpAccounts?: (string | MarketplaceType)[],
  isAllMpAccountsSelected?: boolean,
  needShowActiveAccounts?: boolean,
) {
  const [needToReset, resetMpAccounts] = useState(false);
  const mpAccountOptions = useSelector(userSelectors.currentUserMpAccountTreeView);
  const mpActiveAccounts = useSelector(userSelectors.currentUserMpAccountActiveTreeView);

  const filteredMpAccounts = () => {
    const mpAccounts = needShowActiveAccounts ? mpActiveAccounts : mpAccountOptions;

    if (!excludedMpAccounts?.length) return mpAccounts;

    const excludedAccounts = excludedMpAccounts.map(String);

    const filterTree = (accounts: any[]): any[] => {
      return accounts
        .map((account) => {
          if (excludedAccounts.includes(String(account.key))) {
            return null;
          }

          let filteredChildren: TreeItemType<string | number>[] | undefined = undefined;

          if (account.items?.length) {
            filteredChildren = filterTree(account.items);
          }

          if (account.items && filteredChildren?.length === 0) {
            return null;
          }

          return {
            ...account,
            items: filteredChildren,
          };
        })
        .filter(Boolean);
    };

    return filterTree(mpAccounts) as TreeItemType<string | number>[];
  };

  const getSelectedAccounts = () => {
    const selectedAccounts = selectedMpAccounts?.map(String);

    return filteredMpAccounts()
      .map((mpAccount) => ({
        ...mpAccount,
        items: mpAccount.items
          ?.map((item) => {
            const selected = Boolean(isAllMpAccountsSelected || selectedAccounts?.includes(String(item.key)));

            return { ...item, selected };
          })
          ?.sort((a, b) => a.value.localeCompare(b.value)),
      }))
      ?.sort((a, b) => a.value.localeCompare(b.value));
  };

  const mpTreeViewItems = useMemo<TreeItemType<number | string>[]>(() => {
    resetMpAccounts(false);
    return getSelectedAccounts();
  }, [mpAccountOptions, mpActiveAccounts, selectedMpAccounts, excludedMpAccounts, needToReset]);

  const [selectedAccountIds, setSelectedAccounts] = useState<(string | number)[]>(() => {
    return treeViewUtils.getSelectedKeys(getSelectedAccounts());
  });

  const selectedAccounts = useMemo(() => {
    return selectedAccountIds.filter((id) => !isNaN(+id)).map((id) => +id);
  }, [selectedAccountIds]);

  return {
    mpTreeViewItems,
    resetMpAccounts,
    selectedAccounts,
    setSelectedAccounts,
  };
} // useMpAccountTreeViewOptions

// Marketplace account pairs
export function useMpAccountOptions(includedMpTypes: MarketplaceType[] = []) {
  const mpAccounts = useSelector(userSelectors.currentUserMpAccounts);

  return useMemo<Pair<number>[]>(() => {
    return mpAccounts
      .filter((x) => !includedMpTypes.length || includedMpTypes.includes(x.marketplaceType))
      .map((account) => ({
        key: account.id,
        value: account.name,
      }));
  }, [mpAccounts]);
}
